// =============================================================================
// process-test-canon — canonical `when` / `then` strings (process-tests P9)
// =============================================================================
//
// A stable, source-span-free string for a test's pin block (`when`) and its
// assertion block (`then`), so a `test` element in the node universe carries
// them as `@when` / `@then` field getters. The base→head diff then reads a
// changed `then` as a declared-site change (spec §9) and a `when test
// changed(@then)` gate fires on it. Document order is preserved — the order of
// `then` assertions is semantically meaningful (positional subsequence), so the
// canonical form must NOT sort.

import type {
  TestAssertion,
  TestPin,
  TestPinValue,
  TestRef,
} from "./v010-ast.js";

/** A ref's dotted spelling, with the inbound `Caller >` prefix when present. */
function canonRef(ref: TestRef): string {
  const path = ref.segments.map((s) => s.value).join(".");
  return ref.caller && ref.caller.length ? `${ref.caller.join(".")}>${path}` : path;
}

function canonPinValue(v: TestPinValue): string {
  switch (v.kind) {
    case "answer":
      return v.word;
    case "fail":
      return v.cause !== undefined ? `fails "${v.cause}"` : "fails";
    case "sequence":
      return v.steps.map(canonPinValue).join(",");
    case "after":
      return `after ${v.duration}`;
    case "takes":
      return `takes ${v.duration}`;
    case "count":
      return String(v.n);
    case "list":
      return `[${v.items.join(",")}]`;
    case "cause":
      return `"${v.value}"`;
  }
}

function canonPin(p: TestPin): string {
  return `${canonRef(p.key)}:${canonPinValue(p.value)}`;
}

function canonAssertion(a: TestAssertion): string {
  switch (a.kind) {
    case "occurrence":
      return (
        canonRef(a.ref) +
        (a.modal ? ` ${a.modal}` : "") +
        (a.count ? ` ${a.count.op}${a.count.n}` : "")
      );
    case "terminator":
      return `${a.which}${a.all ? " all" : ""}${a.label !== undefined ? ` "${a.label}"` : ""}`;
    case "time":
      return `time ${a.axis} ${a.op} ${a.duration}`;
    case "parallelGroup":
      return `parallel{${a.assertions.map(canonAssertion).join("; ")}}`;
  }
}

// =============================================================================
// Governance getter values — @tested / @testCoverage (leaf, no runTests dep)
// =============================================================================

/** The scalar values of the `@tested` / `@testCoverage` builtins for a process
 *  subject. `tested` = the process has ≥1 PASSING test; `testCoverage` = its
 *  choice-point coverage percentage (the §11 headline metric), 0 when untested. */
export interface TestGetters {
  readonly tested: boolean;
  readonly testCoverage: number;
}

/** Resolve a subject's test getters by its qualified key; undefined for a
 *  non-process subject. Injected on {@link
 *  import("./selector-eval.js").EvalContext} (mirroring {@link
 *  import("./process-latency.js").ProcessLatencyResolver}) so `@tested` /
 *  `@testCoverage` evaluate in `where`/policy expressions. Built by
 *  `buildProcessTestResolver` (process-test-run.ts). */
export type ProcessTestResolver = (subjectKey: string) => TestGetters | undefined;

/** The scalar value of `@tested` / `@testCoverage` given the subject's resolved
 *  getters (undefined for a non-process subject). Mirrors `latencyBuiltinValue`. */
export function testBuiltinValue(name: string, g: TestGetters | undefined): number | boolean | undefined {
  if (!g) return undefined;
  if (name === "tested") return g.tested;
  if (name === "testCoverage") return g.testCoverage;
  return undefined;
}

/** Canonical `when` block string (the ordered pin list). */
export function canonWhen(pins: readonly TestPin[]): string {
  return pins.map(canonPin).join("; ");
}

/** Canonical `then` block string (the ordered assertion list). */
export function canonThen(assertions: readonly TestAssertion[]): string {
  return assertions.map(canonAssertion).join("; ");
}
